<template>
    <div class="gallery-container container fade-in">
        <div class="gallery-header">
            <h1>{{ $t('gallery.title') }}</h1>
            <p>{{ $t('gallery.subtitle') }}</p>
        </div>

        <div class="empty-state card" v-if="savedFaces.length === 0">
            <div class="empty-icon">🖼️</div>
            <h3>{{ $t('gallery.noAvatars') }}</h3>
            <p>{{ $t('gallery.noAvatarsDesc') }}</p>
            <router-link to="/generator" class="btn btn-primary">{{ $t('gallery.createAvatarBtn') }}</router-link>
        </div>

        <transition-group name="gallery-animation" tag="div" class="gallery-grid" v-else>
            <div class="gallery-item card" v-for="face in savedFaces" :key="face.id">
                <div class="avatar-preview" @click="downloadAvatar(face.data)">
                    <span v-html="face.data"></span>

                </div>
                <div class="avatar-actions">
                    <button @click="downloadAvatar(face.data)" class="action-btn download-btn"
                        :title="$t('gallery.download')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        <span class="btn-text">{{ $t('gallery.download') }}</span>
                    </button>
                    <button @click="shareAvatar(face.data)" class="action-btn share-btn" :title="$t('gallery.share')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="18" cy="5" r="3"></circle>
                            <circle cx="6" cy="12" r="3"></circle>
                            <circle cx="18" cy="19" r="3"></circle>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                        </svg>
                        <span class="btn-text">{{ $t('gallery.share') }}</span>
                    </button>
                    <button @click="deleteFace(face.id)" class="action-btn delete-btn" :title="$t('gallery.delete')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        <span class="btn-text">{{ $t('gallery.delete') }}</span>
                    </button>
                </div>
                <div class="avatar-date">{{ formatDate(face.timestamp) }}</div>
            </div>
        </transition-group>

        <!-- Delete confirmation dialog -->
        <transition name="dialog-fade">
            <div class="delete-dialog" v-if="showDeleteDialog">
                <div class="delete-dialog-content card">
                    <h3>{{ $t('gallery.deleteConfirm') }}</h3>
                    <p>{{ $t('gallery.deleteConfirmText') }}</p>
                    <div class="dialog-buttons">
                        <button @click="confirmDelete" class="btn btn-danger">{{ $t('gallery.confirmDelete') }}</button>
                        <button @click="cancelDelete" class="btn btn-secondary">{{ $t('gallery.cancel') }}</button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { logEvent } from 'firebase/analytics'
import { analytics } from '../firebase'

export default {
    name: 'Gallery',
    metaInfo() {
        return {
            title: this.$t('gallery.title') + ' - Ugly Avatar',
            meta: [
                { name: 'description', content: this.$t('gallery.subtitle') }
            ]
        };
    },
    setup() {
        const store = useStore();
        const { t } = useI18n();
        const showDeleteDialog = ref(false);
        const deleteId = ref(null);

        // Get avatar collection from localStorage
        const savedFaces = computed(() => {
            const faces = JSON.parse(localStorage.getItem('uglyAvatarCollection') || '[]');
            return faces.map((data, idx) => ({
                id: idx,
                data,
                timestamp: Date.now() // or stored timestamp
            }));
        });

        const deleteFace = (id) => {
            deleteId.value = id;
            showDeleteDialog.value = true;

            // 记录打开删除对话框事件
            logEvent(analytics, 'open_delete_dialog');
        };

        const confirmDelete = () => {
            let faces = JSON.parse(localStorage.getItem('uglyAvatarCollection') || '[]');
            faces.splice(deleteId.value, 1);
            localStorage.setItem('uglyAvatarCollection', JSON.stringify(faces));
            showDeleteDialog.value = false;
            // Force refresh to update the UI
            window.location.reload();

            // 记录删除头像事件
            logEvent(analytics, 'delete_avatar');
        };

        const cancelDelete = () => {
            showDeleteDialog.value = false;
        };

        const downloadAvatar = (svgData) => {
            // 记录从收藏下载头像事件
            logEvent(analytics, 'download_from_gallery');

            // 显示下载中状态
            const downloadingToast = document.createElement('div');
            downloadingToast.className = 'downloading-toast';
            downloadingToast.textContent = t('toast.preparing');
            document.body.appendChild(downloadingToast);

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = document.createElement("img");

            canvas.width = 500;
            canvas.height = 500;

            img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
            img.onload = function () {
                ctx.drawImage(img, 0, 0, 500, 500);
                const a = document.createElement("a");
                const e = new MouseEvent("click");
                a.download = "ugly-avatar.png";
                a.href = canvas.toDataURL("image/png");

                // 移除下载中提示
                document.body.removeChild(downloadingToast);

                // 触发下载
                a.dispatchEvent(e);
            };

            // 如果图片加载失败
            img.onerror = function () {
                document.body.removeChild(downloadingToast);
                const errorToast = document.createElement('div');
                errorToast.className = 'error-toast';
                errorToast.textContent = t('toast.failed');
                document.body.appendChild(errorToast);

                setTimeout(() => {
                    document.body.removeChild(errorToast);
                }, 3000);
            };
        }

        const shareAvatar = (svgData) => {
            // 记录从收藏分享头像事件
            logEvent(analytics, 'share_from_gallery');

            // Create a temporary canvas to generate PNG
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = document.createElement("img");

            canvas.width = 500;
            canvas.height = 500;

            img.setAttribute("src", "data:image/svg+xml;base64," + btoa(svgData));
            img.onload = function () {
                ctx.drawImage(img, 0, 0, 500, 500);
                const dataUrl = canvas.toDataURL("image/png");

                // Try native sharing if available
                if (navigator.share) {
                    canvas.toBlob(async (blob) => {
                        try {
                            await navigator.share({
                                title: 'My Ugly-Cute Avatar',
                                text: 'Check out this avatar I created!',
                                files: [new File([blob], 'ugly-avatar.png', { type: 'image/png' })]
                            });
                        } catch (error) {
                            console.error('Error sharing:', error);
                            // Fallback to download if sharing fails
                            downloadAvatar(svgData);
                        }
                    });
                } else {
                    // Fallback for browsers without native sharing
                    window.open(dataUrl);
                }
            };
        };

        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        // 记录页面访问
        logEvent(analytics, 'screen_view', {
            screen_name: 'gallery'
        });
        
        return {
            savedFaces,
            deleteFace,
            downloadAvatar,
            shareAvatar,
            formatDate,
            showDeleteDialog,
            confirmDelete,
            cancelDelete
        }
    }
}
</script>

<style scoped>
.gallery-container {
    padding-top: 2rem;
    padding-bottom: 4rem;
}

.gallery-header {
    text-align: center;
    margin-bottom: 3rem;
}

.gallery-header h1 {
    font-size: 2.5rem;
    color: var(--secondary-color);
    margin-bottom: 0.75rem;
}

.gallery-header p {
    color: var(--text-secondary);
    font-size: 1.1rem;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
}

.empty-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
}

.empty-state h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--secondary-color);
}

.empty-state p {
    color: var(--text-secondary);
    margin-bottom: 2rem;
    max-width: 400px;
}

.gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    gap: 2rem;
}

.gallery-item {
    overflow: hidden;
    transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: var(--border-radius-lg);
    background-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.gallery-item:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
    z-index: 1;
}

.avatar-preview {
    height: 250px;
    background-color: #f9f9f9;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    position: relative;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.2s ease;
    border-bottom: 1px solid #f0f0f0;
}

.avatar-preview:hover {
    background-color: #f5f5f5;
}

.avatar-actions {
    display: flex;
    justify-content: space-between;
    padding: 0.8rem;
    background-color: white;
    transition: all 0.2s ease;
    border-top: 1px solid #f8f8f8;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border: none;
    background: transparent;
    border-radius: var(--border-radius-md);
    cursor: pointer;
    color: var(--text-secondary);
    transition: all 0.2s ease;
    flex: 1;
    gap: 0.5rem;
}

.action-btn svg {
    transition: transform 0.2s ease;
}

.action-btn:hover {
    background-color: #f5f5f5;
    color: var(--primary-color);
}

.action-btn:hover svg {
    transform: translateY(-2px);
}

.download-btn:hover {
    color: #4285F4;
}

.share-btn:hover {
    color: #34A853;
}

.delete-btn:hover {
    color: var(--accent-color);
}

.btn-text {
    font-size: 0.8rem;
    font-weight: 500;
    display: none;
}

.gallery-item:hover .btn-text {
    display: inline-block;
}

.avatar-date {
    font-size: 0.8rem;
    color: var(--text-secondary);
    padding: 0.5rem 0.8rem;
    text-align: right;
    background-color: #fafafa;
    border-top: 1px solid #f0f0f0;
}

.downloading-toast,
.error-toast {
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 24px;
    border-radius: 50px;
    color: white;
    font-weight: 500;
    z-index: 9999;
    animation: fadeInOut 0.3s ease;
}

.downloading-toast {
    background-color: var(--secondary-color);
}

.error-toast {
    background-color: var(--accent-color);
}

@keyframes fadeInOut {
    from {
        opacity: 0;
        transform: translate(-50%, 20px);
    }

    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}

/* Delete dialog styles */
.delete-dialog {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 1rem;
}

.delete-dialog-content {
    width: 100%;
    max-width: 400px;
    padding: 2rem;
    text-align: center;
}

.delete-dialog-content h3 {
    margin-bottom: 1rem;
    color: var(--secondary-color);
}

.delete-dialog-content p {
    margin-bottom: 2rem;
    color: var(--text-secondary);
}

.dialog-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
}

.btn-danger {
    background-color: var(--accent-color);
    color: white;
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
    transition: all 0.3s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .gallery-grid {
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 1.5rem;
    }

    .gallery-header h1 {
        font-size: 2rem;
    }

    .avatar-preview {
        height: 200px;
    }

    .btn-text {
        display: none !important;
    }

    .action-btn {
        padding: 0.6rem;
    }

    .avatar-actions {
        padding: 0.6rem;
    }
}

@media (max-width: 480px) {
    .gallery-grid {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 1rem;
    }

    .avatar-preview {
        height: 160px;
    }

    .action-btn svg {
        width: 18px;
        height: 18px;
    }
}
</style>
